import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getLeagueRankingForProfessor } from '../../../Api/authApi.js.js';
import { formatPrice } from '../../../functions/price-formater.js';
import { Skeleton } from '../../components/Skeleton';
import './vue-ensemble.css';

const fmtPct = (n) => {
    const v = parseFloat(n) || 0;
    return (v >= 0 ? '+' : '') + v.toFixed(2) + ' %';
};

const ICON_PATHS = {
    users: 'M8 11a3 3 0 100-6 3 3 0 000 6zM3 20c0-3 2.2-5 5-5s5 2 5 5M16 11a2.6 2.6 0 100-5.2M15 15.2c2.4.3 4 1.9 4 4.8',
    file: 'M6 3.5h8l4 4V20.5H6V3.5zM14 3.5V8h4',
    trophy: 'M7 4h10v3a5 5 0 01-10 0V4zM7 5H4v2a3 3 0 003 3M17 5h3v2a3 3 0 01-3 3M12 12v4m-3 4h6m-3 0v-4',
};

function StatIcon({ name, size = 17 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d={ICON_PATHS[name] || ''} />
        </svg>
    );
}

function ChangeText({ value, size = 13 }) {
    const v = parseFloat(value) || 0;
    return (
        <span className="mono num" style={{ color: v >= 0 ? 'var(--up)' : 'var(--down)', fontSize: size, fontWeight: 600 }}>
            {fmtPct(v)}
        </span>
    );
}

function RankBadge({ rank }) {
    const colors = { 1: '#E0C281', 2: '#C7CEDB', 3: '#C98A5C' };
    const c = colors[rank];
    return (
        <div className="mono" style={{
            width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, flexShrink: 0,
            background: c ? `${c}22` : 'rgba(255,255,255,0.05)', color: c || 'var(--text-dim)',
            border: `1px solid ${c ? c + '44' : 'var(--border)'}`,
        }}>{rank}</div>
    );
}

function TeamGlobalViewCard({ team, rank, onClick }) {
    const capitalTotal = (team.members || []).reduce((s, m) => s + (parseFloat(m.capital) || 0), 0);
    const memberCount = (team.members || []).length;
    const openPositions = team.open_positions_count ?? 0;

    return (
        <div className="relief pill-tap" style={{ padding: 18, borderRadius: 16, cursor: 'pointer' }} onClick={onClick}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <RankBadge rank={rank} />
            </div>
            <div style={{ fontSize: 16.5, fontWeight: 600, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {team.team_name}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-mute)', marginBottom: 14 }}>
                {memberCount} élève{memberCount > 1 ? 's' : ''} · {openPositions} position{openPositions > 1 ? 's' : ''}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span className="mono num" style={{ fontSize: 19, fontWeight: 700 }}>{formatPrice(capitalTotal)}</span>
                <ChangeText value={team.total_montant} size={14} />
            </div>
        </div>
    );
}

function VueEnsembleSkeleton() {
    return (
        <>
            <div className="prof-vue-ensemble-stats" style={{ marginTop: 16 }}>
                {[0, 1, 2, 3].map(i => (
                    <div key={i} className="relief" style={{ padding: 18, borderRadius: 16 }}>
                        <div style={{ marginBottom: 14 }}><Skeleton width={17} height={17} radius={4} /></div>
                        <Skeleton width={40} height={24} radius={6} />
                        <div style={{ marginTop: 8 }}><Skeleton width={70} height={12} radius={4} /></div>
                    </div>
                ))}
            </div>

            <div style={{ margin: '26px 0 14px' }}>
                <Skeleton width={140} height={16} radius={4} />
            </div>
            <div className="prof-vue-ensemble-teams">
                {[0, 1, 2].map(i => (
                    <div key={i} className="relief" style={{ padding: 18, borderRadius: 16 }}>
                        <div style={{ marginBottom: 12 }}><Skeleton width={28} height={28} radius={8} /></div>
                        <div style={{ marginBottom: 4 }}><Skeleton width="70%" height={16.5} radius={4} /></div>
                        <div style={{ marginBottom: 14 }}><Skeleton width="50%" height={12.5} radius={4} /></div>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                            <Skeleton width={70} height={19} radius={4} />
                            <Skeleton width={40} height={14} radius={4} />
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

const VueEnsemble = () => {
    const professeurId = useSelector((state) => state.auth.user?.id);
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | ready | empty | error

    const load = useCallback(() => {
        if (!professeurId) return;
        setStatus('loading');
        getLeagueRankingForProfessor(professeurId)
            .then(res => {
                setData(res);
                setStatus((res?.teams || []).length ? 'ready' : 'empty');
            })
            .catch(() => setStatus('error'));
    }, [professeurId]);

    useEffect(() => { load(); }, [load]);

    const teams = data?.teams || [];
    const totalStudents = teams.reduce((s, t) => s + (t.members || []).length, 0);
    const totalOpenPositions = teams.reduce((s, t) => s + (t.open_positions_count || 0), 0);
    const sessionLabel = (data?.session_day && data?.session_total_days)
        ? `${data.session_day}/${data.session_total_days}`
        : '—';

    const stats = [
        { label: teams.length > 1 ? 'Équipes' : 'Équipe', value: teams.length, icon: 'users' },
        { label: totalStudents > 1 ? 'Élèves' : 'Élève', value: totalStudents, icon: 'users' },
        { label: (data?.session_total_days ?? 0) > 1 ? 'Jours de session' : 'Jour de session', value: sessionLabel, icon: 'file' },
        { label: totalOpenPositions > 1 ? 'Positions ouvertes' : 'Position ouverte', value: totalOpenPositions, icon: 'trophy' },
    ];

    return (
        <div className="prof-vue-ensemble-page">
            <div style={{ padding: '4px 4px 4px' }}>
                {status === 'loading' ? (
                    <Skeleton width={220} height={28} radius={6} />
                ) : (
                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em' }}>
                        {data?.league_name || "Vue d'ensemble"}
                    </h1>
                )}
            </div>

            {status === 'loading' && <VueEnsembleSkeleton />}
            {status === 'empty' && (
                <div className="relief" style={{ marginTop: 16, borderRadius: 16, padding: 20, textAlign: 'center', color: 'var(--text-mute)', fontSize: 13 }}>
                    Aucune équipe trouvée pour votre ligue.
                </div>
            )}
            {status === 'error' && (
                <div className="relief" style={{ marginTop: 16, borderRadius: 16, padding: 20, textAlign: 'center', color: 'var(--down)', fontSize: 13 }}>
                    Impossible de charger les données de la ligue.
                </div>
            )}

            {status === 'ready' && (
                <>
                    <div className="prof-vue-ensemble-stats" style={{ marginTop: 16 }}>
                        {stats.map(s => (
                            <div key={s.label} className="relief" style={{ padding: 18, borderRadius: 16 }}>
                                <div style={{ color: 'var(--text-mute)', marginBottom: 14 }}><StatIcon name={s.icon} /></div>
                                <div className="mono num" style={{ fontSize: 24, fontWeight: 700 }}>{s.value}</div>
                                <div style={{ fontSize: 12, color: 'var(--text-mute)', marginTop: 4 }}>{s.label}</div>
                            </div>
                        ))}
                    </div>

                    <div style={{ margin: '26px 0 14px', fontSize: 16, fontWeight: 600 }}>
                        Équipes de la ligue
                    </div>
                    <div className="prof-vue-ensemble-teams">
                        {teams.map((team, i) => (
                            <TeamGlobalViewCard
                                key={team.team_id}
                                team={team}
                                rank={i + 1}
                                onClick={() => navigate(`/espace-professeur/equipes?team=${team.team_id}`)}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default VueEnsemble;
